import { createElement, type KeyboardEvent, type ReactElement, type ReactNode, useId, useRef, useState } from 'react';
import { UiIcon } from './icons';
import { cx } from './library-kit';

export interface AccordionItem {
  readonly id: string;
  /** The header text (inside the heading's button). */
  readonly title: ReactNode;
  readonly content: ReactNode;
  readonly disabled?: boolean;
}

export interface AccordionProps {
  readonly items: readonly AccordionItem[];
  /** Controlled: the open item ids. Omit for an uncontrolled accordion that starts at `defaultOpen`. */
  readonly openIds?: readonly string[];
  readonly defaultOpen?: readonly string[];
  readonly onChange?: (openIds: readonly string[]) => void;
  /** Allow more than one item open at once (default: opening one closes the others). */
  readonly multiple?: boolean;
  /** The headings' level. Default 3. */
  readonly headingLevel?: 2 | 3 | 4;
  readonly className?: string;
}

/**
 * The accordion (Design System FX-14): a stack of disclosure sections. The header is a real `<button aria-expanded
 * aria-controls>` inside a heading, 48px high; the panel grows over 320ms and fades over 200ms while the chevron turns 180° in
 * 200ms. A closed panel is `inert` + `visibility: hidden`, so its content is out of the tab order and of the accessibility tree.
 * Up/Down/Home/End move between headers. The panel growth is an FX class: stilled under `prefers-reduced-motion`.
 */
export function Accordion({ items, openIds, defaultOpen = [], onChange, multiple = false, headingLevel = 3, className }: AccordionProps): ReactElement {
  const base = useId();
  const [inner, setInner] = useState<readonly string[]>(defaultOpen);
  const open = openIds ?? inner;
  const refs = useRef(new Map<string, HTMLButtonElement>());
  const toggle = (id: string): void => {
    const isOpen = open.includes(id);
    const next = isOpen ? open.filter((o) => o !== id) : multiple ? [...open, id] : [id];
    if (openIds === undefined) setInner(next);
    onChange?.(next);
  };
  const enabled = items.filter((i) => i.disabled !== true);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>): void => {
    const at = enabled.findIndex((i) => refs.current.get(i.id) === document.activeElement);
    if (at < 0) return;
    let to: AccordionItem | undefined;
    if (e.key === 'ArrowDown') to = enabled[(at + 1) % enabled.length];
    else if (e.key === 'ArrowUp') to = enabled[(at - 1 + enabled.length) % enabled.length];
    else if (e.key === 'Home') to = enabled[0];
    else if (e.key === 'End') to = enabled[enabled.length - 1];
    else return;
    e.preventDefault();
    if (to !== undefined) refs.current.get(to.id)?.focus();
  };
  return (
    <div className={cx('vello-acc', className)} onKeyDown={onKeyDown}>
      {items.map((item) => {
        const expanded = open.includes(item.id);
        const triggerId = `${base}-${item.id}-trigger`;
        const panelId = `${base}-${item.id}-panel`;
        return (
          <div key={item.id} className="vello-acc__item">
            {createElement(
              `h${headingLevel}`,
              { className: 'vello-acc__head' },
              <button
                ref={(el) => {
                  if (el === null) refs.current.delete(item.id);
                  else refs.current.set(item.id, el);
                }}
                type="button"
                id={triggerId}
                className="vello-acc__trigger"
                aria-expanded={expanded}
                aria-controls={panelId}
                disabled={item.disabled}
                onClick={() => toggle(item.id)}
              >
                <span>{item.title}</span>
                <span className="vello-acc__chevron" aria-hidden="true">
                  <UiIcon name="chevronDown" size={16} />
                </span>
              </button>,
            )}
            <div id={panelId} role="region" aria-labelledby={triggerId} className="vello-acc__panel" data-open={expanded ? 'true' : 'false'} inert={!expanded}>
              <div className="vello-acc__inner">
                <div className="vello-acc__body">{item.content}</div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
